/**
 * Recently Viewed
 * ------------------------------------------------------------------
 * Replaces the six hardcoded products the profile stack used to render.
 *
 * Two jobs, both driven off this browser only:
 *   1. On a product route, `layout/theme.liquid` emits a small JSON payload.
 *      We push it to the front of a capped localStorage list.
 *   2. On any page carrying `[data-recently-viewed]`, render that list as the
 *      same `.pf-profile__product` cards the rail already styles, and toggle
 *      the sibling `[data-recently-viewed-empty]` note.
 *
 * There is no server state and no app dependency: a shopper who has viewed
 * nothing correctly sees an empty rail rather than invented products.
 */
(function () {
  'use strict';

  var KEY = 'pf:recently-viewed:v1';
  var MAX = 24;

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(function (i) { return i && i.handle; }) : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    } catch (e) {
      /* private mode, quota, or storage disabled — the rail just stays empty */
    }
  }

  function record() {
    var node = document.querySelector('[data-pf-recently-viewed-product]');
    if (!node) return;

    var item;
    try {
      item = JSON.parse(node.textContent);
    } catch (e) {
      return;
    }
    if (!item || !item.handle) return;

    item.viewedAt = Date.now();

    var list = read().filter(function (existing) {
      return existing.handle !== item.handle;
    });
    list.unshift(item);
    write(list);
  }

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Current, server-rendered card presentation keyed by handle. Browser history
  // can outlive a price or media change, so image, price and URL are reconciled
  // together rather than replacing only the art and creating a size mismatch.
  var catalogPresentations = null;
  function presentationFor(item) {
    if (catalogPresentations === null) {
      catalogPresentations = {};
      var node = document.querySelector('[data-recently-viewed-images]');
      if (node) {
        try { catalogPresentations = JSON.parse(node.textContent) || {}; } catch (e) { catalogPresentations = {}; }
      }
    }
    var current = item && item.handle ? catalogPresentations[item.handle] : null;
    if (typeof current === 'string') return { image: current };
    return current && typeof current === 'object' ? current : {};
  }

  function card(item, grid, cta) {
    var current = presentationFor(item);
    var image = current.image || item.image;
    var media = image
      ? '<img class="pf-rv-card__front" src="' + esc(image) + '" alt="" loading="lazy" width="167" height="242">'
      : '<span aria-hidden="true"></span>';
    var href = esc(current.url || item.url || '/products/' + item.handle);
    var price = current.price || item.price;
    var hoverImage = current.hoverImage || item.hoverImage;
    var name = esc(item.titleName || item.title);
    var category = item.titleCategory ? '<span class="pf-profile__product-category">' + esc(item.titleCategory) + '</span>' : '';
    var hover = hoverImage
      ? '<span class="pf-rv-hover" aria-hidden="true">' +
          '<img class="pf-rv-hover__scene" src="' + esc(hoverImage) + '" alt="" loading="lazy">' +
          '<span class="pf-rv-hover__panel">' +
            '<strong class="pf-rv-hover__title"><span>' + name + '</span>' + category + '</strong>' +
            (item.subtitle ? '<span class="pf-rv-hover__subtitle">' + esc(item.subtitle) + '</span>' : '') +
            (price ? '<small class="pf-rv-hover__price">' + esc(price) + '</small>' : '') +
            '<span class="pf-rv-hover__cta">' + esc(cta) + '</span>' +
          '</span>' +
        '</span>'
      : '';

    if (grid) {
      var hoverMedia = hoverImage
        ? '<img class="pf-rv-card__hover-image" src="' + esc(hoverImage) + '" alt="" loading="lazy">'
        : '';
      return (
        '<article class="pf-rv-card' + (hover ? ' pf-rv-card--lifestyle' : '') + '">' +
          '<a class="pf-rv-card__link" href="' + href + '" aria-label="' + esc(item.title) + '"></a>' +
          '<div class="pf-rv-card__rest">' +
            '<div class="pf-rv-card__media">' + media + hoverMedia + '</div>' +
            '<div class="pf-rv-card__info">' +
              '<h2><span>' + name + '</span>' + category + '</h2>' +
              '<p>' + esc(item.subtitle || '') + '</p>' +
              (price ? '<small>' + esc(price) + '</small>' : '') +
              '<span class="pf-rv-card__cta" aria-hidden="true">' + esc(cta) + ' &#8594;</span>' +
            '</div>' +
          '</div>' +
          hover +
        '</article>'
      );
    }

    return (
      '<a class="pf-profile__product' + (hover ? ' pf-profile__product--lifestyle' : '') + '" href="' + href + '">' +
        '<span class="pf-profile__product-media">' + media + '</span>' +
        '<strong>' +
          '<span class="pf-profile__product-name">' + name + '</span>' +
          category +
        '</strong>' +
        (item.subtitle ? '<span>' + esc(item.subtitle) + '</span>' : '<span></span>') +
        (price ? '<small>' + esc(price) + '</small>' : '') +
        hover +
      '</a>'
    );
  }

  function renderInto(root) {
    var containers = root.querySelectorAll('[data-recently-viewed]');
    if (!containers.length) return;

    var list = read();

    Array.prototype.forEach.call(containers, function (container) {
      var limit = parseInt(container.getAttribute('data-recently-viewed-limit'), 10) || 6;
      var slice = list.slice(0, limit);

      // The rail sits next to its own empty note; find the nearest one.
      var scope = container.parentNode || document;
      var empty = scope.querySelector('[data-recently-viewed-empty]');

      if (!slice.length) {
        container.hidden = true;
        container.innerHTML = '';
        if (empty) empty.hidden = false;
        return;
      }

      var grid = container.hasAttribute('data-recently-viewed-grid');
      var cta = container.getAttribute('data-recently-viewed-cta') || '';
      container.innerHTML = slice.map(function (item) { return card(item, grid, cta); }).join('');
      container.hidden = false;
      if (empty) empty.hidden = true;
    });
    document.dispatchEvent(new CustomEvent('pf:recently-viewed:rendered'));
  }

  function boot(root) {
    renderInto(root || document);
  }

  record();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  // Theme Editor lifecycle: a re-rendered section loses its cards otherwise.
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });
})();
